import toast from "react-hot-toast";
import {
  useAssociateCompanyDealMutation,
  useCompanyDealOptions,
  useContactDealOptions,
  useDisassociateCompanyDealMutation,
  useLinkDealMutation,
  useUnlinkDealMutation,
} from "service/crm";
import { DealLinkScope, DealOption } from "types/crm/deal";

const OPTIONS_LIMIT = 20;

/**
 * Resolves the "Add existing deal" data source and link/unlink behaviour for
 * whichever entity opened the modal. Every hook below runs on every render —
 * only `enabled` and the returned result differ — so adding a scope never
 * changes hook order.
 */
export const useDealLinkScope = (
  scope: DealLinkScope | undefined,
  search: string,
  enabled: boolean,
) => {
  const isContact = scope?.type === "contact";
  const isCompany = scope?.type === "company";
  const params = { search: search || undefined, limit: OPTIONS_LIMIT };

  const contactOptions = useContactDealOptions(
    scope?.type === "contact" ? scope.uuid : "",
    params,
    { enabled: enabled && isContact },
  );

  const companyOptions = useCompanyDealOptions(
    scope?.type === "company" ? scope.uuid : "",
    params,
    { enabled: enabled && isCompany },
  );

  const linkContact = useLinkDealMutation();
  const unlinkContact = useUnlinkDealMutation();
  const linkCompany = useAssociateCompanyDealMutation();
  const unlinkCompany = useDisassociateCompanyDealMutation();

  const mutations = [linkContact, unlinkContact, linkCompany, unlinkCompany];

  const toggleLink = async (deal: DealOption) => {
    if (!scope) return;
    const linked = deal.associated;

    try {
      if (scope.type === "contact") {
        const mutation = linked ? unlinkContact : linkContact;
        await mutation.mutateAsync({
          contactUuid: scope.uuid,
          dealUuid: deal.uuid,
        });
      } else {
        const mutation = linked ? unlinkCompany : linkCompany;
        await mutation.mutateAsync({
          companyUuid: scope.uuid,
          dealUuid: deal.uuid,
        });
      }
      toast.success(linked ? "Deal unlinked" : "Deal linked");
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to update deal link. Please try again.",
      );
    }
  };

  return {
    ...(isContact ? contactOptions : companyOptions),
    toggleLink,
    isTogglePending: (dealUuid: string) =>
      mutations.some(
        (mutation) =>
          mutation.isPending && mutation.variables?.dealUuid === dealUuid,
      ),
  };
};
